// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The building model's walls and openings as the locked plan draws them
 * (frontend-4rt): each wall as its outline (centre line ± half its
 * thickness, cut where it meets another wall: corners mitred, a tee's stem
 * up to the through wall's face — `wallJoins.ts`, frontend-5yu) and axis, each opening as the cut it makes in its host and an
 * architect's symbol — a door's leaf and swing, a window's panes — with a
 * Danish label. World x/y; `routes/bygning/PlanWalls.tsx` projects them.
 * Pure: no React, no DOM.
 */

import type { SolidOpening, SolidWall } from '../api/types';
import { readSwing } from './openingSwing';
import { formatLength } from './ruler';
import { joinedFootprints } from './wallJoins';

export type XY = readonly [number, number];

export interface PlanWallShape {
  guid: string;
  /** The wall's footprint (four corners), or its axis alone (two) when the thickness is unknown. */
  outline: XY[];
  axis: [XY, XY];
  external: boolean;
  /** The thickness is a default, not measured: drawn dashed. */
  assumed: boolean;
  /** World z the plan draws it at (its base). */
  z: number;
  /** The plan's cut goes through it: drawn as solid poché. */
  cut: boolean;
  /** Wholly under the cut (a parapet, the storeys below under Etage N og under): a thin line seen beyond. */
  beyond: boolean;
}

export interface PlanOpeningShape {
  guid: string;
  kind: SolidOpening['kind'];
  /** The cut through the host, four corners (two when the host has no thickness). */
  cut: XY[];
  /**
   * A door's leaves, each from its hinge out, and their swings (quarter
   * arcs, from open to shut) — one, or two for a double door; none for
   * the others. Hinge and side from `openingSwing.readSwing`.
   */
  leaves: [XY, XY][];
  swings: XY[][];
  /** A window's panes: lines along the wall inside the cut. */
  panes: [XY, XY][];
  /** The opening's middle, on the axis. */
  centre: XY;
  /** Where the label goes: off the wall on the side a door does not swing into. */
  labelAt: XY;
  /** Its width along the wall, metres. */
  width: number;
  label: string;
  z: number;
}

export interface PlanWallSet {
  walls: PlanWallShape[];
  openings: PlanOpeningShape[];
  /** Several storeys and no focus: drawn as faint outlines only, no labels. */
  ghost: boolean;
}

const KIND_LABEL: Record<SolidOpening['kind'], string> = { door: 'Dør', window: 'Vindue', opening: 'Åbning' };

/** "Dør · 0,90 m". */
export function openingLabel(o: Pick<SolidOpening, 'kind' | 's_start' | 's_end'>): string {
  return `${KIND_LABEL[o.kind]} · ${formatLength(Math.abs(o.s_end - o.s_start))}`;
}

function frame(w: SolidWall): { d: XY; n: XY } | null {
  const dx = w.end[0] - w.start[0];
  const dy = w.end[1] - w.start[1];
  const len = Math.hypot(dx, dy);
  if (!(len > 1e-6)) return null;
  return { d: [dx / len, dy / len], n: [-dy / len, dx / len] };
}

const off = (p: XY, n: XY, s: number): XY => [p[0] + n[0] * s, p[1] + n[1] * s];

export type VerticalRelation = 'cut' | 'below' | 'above';

/** Where an element from `zBase` to `zTop` sits against a plan cut at world height `cutZ`. */
export function verticalRelation(zBase: number, zTop: number, cutZ: number): VerticalRelation {
  if (zTop < cutZ) return 'below';
  if (zBase > cutZ) return 'above';
  return 'cut';
}

/**
 * The walls and openings to draw for storey `storeyId` (null = no storey
 * focus), by the same rule as the spaces (`planSpaces`): a focus shows its
 * storey's; without one a single-storey model shows everything, a
 * multi-storey one every wall faintly.
 *
 * With the plan's cut height `cutZ` (Etage, `storeyClip.planScope`), the
 * plan follows drawing conventions: the focused storey's walls the cut
 * goes through are poché, a wall wholly under it is a thin line seen
 * beyond, one wholly over it is left out; the walls of other storeys that
 * were passed in (Etage N og under) are drawn as seen beyond when they lie
 * under the cut. Openings are drawn in cut walls only. Without `cutZ`
 * every wall of the shown storey counts as cut.
 */
export function planWallSet(
  walls: readonly SolidWall[] | null | undefined,
  openings: readonly SolidOpening[] | null | undefined,
  storeyId: number | null,
  cutZ: number | null = null,
): PlanWallSet {
  const all = walls ?? [];
  const storeys = new Set(all.map((w) => w.storey_id));
  const keep = (sid: number | null) => storeyId === null || sid === storeyId;
  const ghost = storeyId === null && storeys.size > 1;
  const byGuid = new Map(all.map((w) => [w.guid, w]));
  // Every wall passed in, so a wall meets its neighbour even when the cut leaves only one drawn as poché.
  const footprints = joinedFootprints(
    all.map((w) => ({ guid: w.guid, start: w.start, end: w.end, half: Math.max(0, (w.thickness ?? 0) / 2), group: w.storey_id })),
  );

  const outWalls: PlanWallShape[] = [];
  const cutWalls = new Set<string>();
  for (const w of all) {
    let cut = !ghost;
    let beyond = false;
    if (cutZ !== null && storeyId !== null) {
      const rel = verticalRelation(w.z_base, w.z_top, cutZ);
      if (rel === 'above' || (!keep(w.storey_id) && rel !== 'below')) continue;
      cut = keep(w.storey_id) && rel === 'cut';
      beyond = !cut;
    } else if (!keep(w.storey_id)) continue;
    const f = frame(w);
    if (!f) continue;
    const a: XY = [w.start[0], w.start[1]];
    const b: XY = [w.end[0], w.end[1]];
    const t = (w.thickness ?? 0) / 2;
    outWalls.push({
      guid: w.guid,
      outline: footprints.get(w.guid) ?? (t > 0 ? [off(a, f.n, t), off(b, f.n, t), off(b, f.n, -t), off(a, f.n, -t)] : [a, b]),
      axis: [a, b],
      external: w.is_external,
      assumed: w.assumed,
      z: w.z_base,
      cut,
      beyond,
    });
    if (cut) cutWalls.add(w.guid);
  }

  const outOpenings: PlanOpeningShape[] = [];
  if (!ghost) {
    for (const o of openings ?? []) {
      if (!o.start || !o.end) continue;
      const host = byGuid.get(o.wall_guid);
      if (!keep(host ? host.storey_id : o.storey_id)) continue;
      // Under a cut, only the walls it goes through show their openings.
      if (cutZ !== null && storeyId !== null && !(host && cutWalls.has(host.guid))) continue;
      const s: XY = [o.start[0], o.start[1]];
      const e: XY = [o.end[0], o.end[1]];
      const width = Math.hypot(e[0] - s[0], e[1] - s[1]);
      if (!(width > 1e-6)) continue;
      const d: XY = [(e[0] - s[0]) / width, (e[1] - s[1]) / width];
      const n: XY = host ? (frame(host)?.n ?? [-d[1], d[0]]) : [-d[1], d[0]];
      const t = host ? (host.thickness ?? 0) / 2 : 0;
      const cut: XY[] = t > 0 ? [off(s, n, t), off(e, n, t), off(e, n, -t), off(s, n, -t)] : [s, e];
      const leaves: [XY, XY][] = [];
      const swings: XY[][] = [];
      const panes: [XY, XY][] = [];
      // Which face the door opens to (along ±n), and the side a label goes on: the other.
      let swingN: XY = n;
      if (o.kind === 'door') {
        const sw = readSwing(o);
        // Side a is the host's left (+n); hinge `start` is the jamb at s_start.
        swingN = sw.side === 'a' ? n : [-n[0], -n[1]];
        const [j0, j1] = [s, e];
        const u: XY = [(j1[0] - j0[0]) / width, (j1[1] - j0[1]) / width];
        const back: XY = [-u[0], -u[1]];
        const jambs: [XY, XY, number][] =
          sw.leaves === 2
            ? [
                [j0, u, width / 2],
                [j1, back, width / 2],
              ]
            : sw.hinge === 'start'
              ? [[j0, u, width]]
              : [[j1, back, width]];
        for (const [jamb, toward, r] of jambs) {
          const hinge = off(jamb, swingN, t);
          leaves.push([hinge, off(hinge, swingN, r)]);
          const arc: XY[] = [];
          for (let i = 0; i <= 12; i += 1) {
            const a = ((i / 12) * Math.PI) / 2;
            // From the open leaf (along the swing normal) round to the shut one (towards the other jamb).
            arc.push([hinge[0] + r * (swingN[0] * Math.cos(a) + toward[0] * Math.sin(a)), hinge[1] + r * (swingN[1] * Math.cos(a) + toward[1] * Math.sin(a))]);
          }
          swings.push(arc);
        }
      } else if (o.kind === 'window') {
        for (const k of t > 0 ? [t, 0, -t] : [0]) panes.push([off(s, n, k), off(e, n, k)]);
      }
      outOpenings.push({
        guid: o.guid,
        kind: o.kind,
        cut,
        leaves,
        swings,
        panes,
        centre: [(s[0] + e[0]) / 2, (s[1] + e[1]) / 2],
        labelAt: off([(s[0] + e[0]) / 2, (s[1] + e[1]) / 2], swingN, -(t + 0.3)),
        width,
        label: openingLabel(o),
        z: host?.z_base ?? o.z_sill,
      });
    }
  }
  return { walls: outWalls, openings: outOpenings, ghost };
}
